/* The one way this app talks to the API.
 *
 * `frontend-react.md`: the web client is an ordinary API consumer using the
 * same endpoints the mobile apps use. There is no web-only data path — that is
 * ADR-0002's first constraint, and the reason a SPA was chosen over a
 * meta-framework with its own server.
 *
 * Every response is `unknown` until a contract schema narrows it. A fetch
 * wrapper that hands back `any` is the largest single hole `lang-typescript.md`
 * exists to close.
 */

import { type ErrorCode, errorResponseSchema } from '@eventuley/contracts';
import type { z } from 'zod';

/* Relative, so the session cookie is same-origin. In development Vite proxies
 * `/api` to the API's port (see vite.config.ts), which means the cookie path
 * exercised locally is the one that ships — a CORS setup used only in
 * development would be testing something else. */
const BASE = '/api';

/** A failure the caller is expected to handle, carrying the API's error code. */
export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    readonly status: number,
    override readonly message: string,
    readonly fields: readonly { path: string; rule: string }[] = [],
    readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** The network never answered. Distinct from the API answering with a failure. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('The request did not reach the server.');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

interface RequestOptions<T extends z.ZodType> {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  /** Narrows the response. Omit for endpoints that return nothing. */
  readonly schema?: T;
  readonly signal?: AbortSignal;
}

export async function request<T extends z.ZodType>(
  path: string,
  options: RequestOptions<T> = {},
): Promise<z.infer<T>> {
  const method = options.method ?? 'GET';

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      /* The session cookie is HttpOnly, so JavaScript cannot read it — this is
       * what makes the browser attach it. `same-origin` rather than `include`:
       * there is no cross-origin case, and `include` would be a broader grant
       * than anything here needs. */
      credentials: 'same-origin',
      headers: {
        ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
        /* The API's Origin check refuses a state-changing cookie request with
         * no Origin. Browsers set this header themselves and it cannot be set
         * from script, which is precisely why it is trustworthy. */
      },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    /* A genuine transport failure — offline, DNS, the server not running.
     * Kept distinct from an API failure so the UI can say something true
     * rather than "something went wrong". */
    throw new NetworkError(cause);
  }

  if (!response.ok) throw await toApiError(response);

  if (response.status === 204) return undefined as z.infer<T>;

  const payload: unknown = await response.json();
  /* Parsed, not cast. The server is trusted to be well-behaved and is still not
   * trusted to be the version this client was built against — a deployed old
   * mobile client hitting a newer API is the case `mobile-expo.md` warns about,
   * and the web client gets the same discipline for free. */
  return options.schema ? options.schema.parse(payload) : (payload as z.infer<T>);
}

async function toApiError(response: Response): Promise<ApiError> {
  const retryAfter = response.headers.get('retry-after');
  const retryAfterSeconds = retryAfter ? Number(retryAfter) : undefined;

  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    /* A non-JSON error body means something in front of the API answered — a
     * proxy, a load balancer, an error page. Not the documented shape, so it is
     * reported as unexpected rather than mis-parsed into a code the UI will
     * branch on. */
    return new ApiError('internal_error', response.status, response.statusText);
  }

  const result = errorResponseSchema.safeParse(parsed);
  if (!result.success) {
    return new ApiError('internal_error', response.status, response.statusText);
  }

  const { code, message, fields } = result.data.error;
  return new ApiError(
    code,
    response.status,
    message,
    fields ?? [],
    Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : undefined,
  );
}
